import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import blogApi from '@/api/blog';
import SEO from '@/components/ui/SEO';
import Loading from '@/components/ui/Loading';
import NotFoundPage from './NotFoundPage';
import BlogPostCard from '@/components/blog/BlogPostCard';
import BlogPagination from '@/components/blog/BlogPagination';

const BlogTagPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1', 10);

  const { data, isLoading, error } = useQuery({
    queryKey: ['blog', 'tag', slug, { page }],
    queryFn: () => blogApi.getTagWithPosts(slug!, { page, pageSize: 12 }),
    enabled: !!slug,
  });

  if (isLoading) return <Loading fullScreen />;
  if (error || !data) return <NotFoundPage />;

  const { tag, posts, totalCount, pageSize } = data;

  return (
    <>
      <SEO
        title={`${tag.name} - Blog`}
        description={`Blog posts tagged with "${tag.name}"`}
      />

      {/* Tag Header */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <div
            className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4"
            style={tag.colorHex
              ? { backgroundColor: `${tag.colorHex}20`, color: tag.colorHex }
              : { backgroundColor: '#f3f4f6', color: '#4b5563' }
            }
          >
            Tag
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{tag.name}</h1>
          <p className="text-gray-600">
            {tag.postCount} {tag.postCount === 1 ? 'post' : 'posts'}
          </p>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="section">
        <div className="container mx-auto px-6 lg:px-12">
          {posts.length === 0 ? (
            <div className="text-center py-12 text-gray-600">
              <p>No posts found for this tag.</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <BlogPostCard key={post.id} post={post} />
                ))}
              </div>

              <BlogPagination page={page} pageSize={pageSize} totalCount={totalCount} />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogTagPage;
